// A form field value can pull the live value of another component instead of a
// literal by holding a reference string:
//
//   component://<component uuid>/value
//
// `component://` marks the value as a component reference. It is followed by the
// uuid of the component whose runtime value should be read, then a `/`-separated
// path. Today only the `value` field is meaningful (the text a visitor typed
// into an input/password field), but the path is preserved so richer component
// state can be addressed later. See formValueResolution for where references are
// resolved against the live value registry.

export const COMPONENT_REFERENCE_PREFIX = "component://";

export interface ComponentReference {
  uuid: string;
  path: string[];
}

// Quick predicate used before the (slightly) more expensive parse.
export function isComponentReference(value: unknown): value is string {
  return typeof value === "string"
    && value.trimStart().startsWith(COMPONENT_REFERENCE_PREFIX);
}

// Parse `component://<uuid>/<field>/<field>` into its uuid and navigation path.
// Returns null for anything that is not a component reference. Path segments are
// URI-decoded so fields containing reserved characters can be encoded. Mirrors
// parseDataSourceReference so the two reference kinds behave the same way.
export function parseComponentReference(
  value: unknown,
): ComponentReference | null {
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed.startsWith(COMPONENT_REFERENCE_PREFIX)) return null;

  const remainder = trimmed.slice(COMPONENT_REFERENCE_PREFIX.length);
  const [rawUuid, ...rawPath] = remainder.split("/");
  const uuid = decodeSegment(rawUuid ?? "");
  // The uuid must be present; empty path segments (trailing or doubled slashes)
  // are dropped so navigation stays well-formed.
  if (!uuid) return null;

  const path = rawPath
    .map(decodeSegment)
    .filter((segment) => segment.length > 0);

  return { uuid, path };
}

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    // Leave malformed percent-encoding untouched rather than throwing.
    return segment;
  }
}
